import { useEffect, useRef } from 'react'
import { useParams } from '@tanstack/react-router'
import { ExternalLink, ChevronRight } from 'lucide-react'
import { Markdown } from '@/components/ui/Markdown'
import { usePermission } from '@/shell/module-system/permissions'
import { HelpSidebar } from '../components/HelpSidebar'
import { HelpSearchBar } from '../components/HelpSearchBar'
import { HelpFeedbackForm } from '../components/HelpFeedbackForm'
import { useHelpManifest, useHelpPage, type PageBreadcrumb } from '../api/queries'

/**
 * /help/$section/$slug[/$subSlug] — render one help page.
 *
 * MasterDetail layout (left tree-nav, right content). Anchor scroll
 * pulls the URL fragment after mount; deep-links like
 * /help/modules/contacts#archive-partner land scrolled to the right
 * heading with a brief highlight pulse.
 */
export function HelpDetailPage() {
  const params = useParams({ strict: false }) as {
    section?: string
    slug?: string
    subSlug?: string
  }

  const { data: manifest } = useHelpManifest()
  const { data: page, isLoading, error } = useHelpPage(
    params.section,
    params.slug,
    params.subSlug,
  )
  const isAdmin = usePermission('admin')
  const contentRef = useRef<HTMLDivElement>(null)

  // Anchor scroll on mount + on hash change.
  useEffect(() => {
    if (!page) return
    const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) : ''
    if (!hash) return
    // Wait one tick so the HTML has rendered.
    requestAnimationFrame(() => {
      const el = document.getElementById(hash)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        // Brief highlight pulse so the user can see where they landed.
        el.classList.add('help-anchor-pulse')
        setTimeout(() => el.classList.remove('help-anchor-pulse'), 1800)
      }
    })
  }, [page])

  return (
    <>
    <div className="flex h-full flex-col bg-canvas">
        {/* Header band */}
        <header className="flex items-center gap-4 border-b border-border-subtle bg-surface px-6 py-3">
          <div className="flex-1 min-w-0">
            {page ? (
              <>
                <Breadcrumbs items={page.breadcrumbs} />
                <h1 className="text-role-page-title truncate">{page.title}</h1>
              </>
            ) : (
              <h1 className="text-role-page-title">Help</h1>
            )}
          </div>
          <div className="w-96">
            <HelpSearchBar />
          </div>
          {isAdmin && page ? (
            <a
              href={page.github_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-sm border border-border-default bg-surface px-2.5 py-1 text-xs font-medium text-secondary hover:bg-surface-hover hover:text-primary"
              title="Edit on GitHub (admin only)"
            >
              <ExternalLink size={12} />
              Edit on GitHub
            </a>
          ) : null}
        </header>

        {/* MasterDetail body */}
        <div className="flex flex-1 overflow-hidden">
          <aside className="w-[360px] flex-shrink-0 overflow-y-auto border-r border-border-subtle bg-surface-sunk">
            {manifest ? <HelpSidebar manifest={manifest} activeUrl={typeof window !== 'undefined' ? window.location.pathname : ''} /> : null}
          </aside>

          <main className="flex flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-8 py-6" ref={contentRef}>
              <article className="mx-auto max-w-3xl">
                {isLoading ? (
                  <div className="text-sm text-tertiary">Loading…</div>
                ) : error ? (
                  <div className="rounded-sm border border-danger-border bg-danger-bg px-4 py-3 text-sm text-danger-text">
                    Couldn't load this help page. The URL may be wrong, or
                    docs may not yet exist for this topic.
                  </div>
                ) : page ? (
                  <>
                    <Markdown asHtml anchorBaseUrl={pathFor(params)}>{page.html}</Markdown>
                    <div className="mt-12 border-t border-border-subtle pt-6">
                      <HelpFeedbackForm
                        section={page.section}
                        slug={page.slug}
                        title={page.title}
                      />
                    </div>
                  </>
                ) : null}
              </article>
            </div>

            {/* Sticky right-rail TOC — only when the doc has enough headings */}
            {page && page.headings.filter((h) => h.level === 2 || h.level === 3).length > 3 ? (
              <aside className="hidden w-[220px] flex-shrink-0 overflow-y-auto border-l border-border-subtle bg-surface-sunk px-4 py-6 lg:block">
                <div className="text-role-section-label mb-2">On this page</div>
                <nav className="flex flex-col gap-1.5 text-xs">
                  {page.headings
                    .filter((h) => h.level === 2 || h.level === 3)
                    .map((h) => (
                      <a
                        key={h.id}
                        href={`#${h.id}`}
                        className={
                          h.level === 3
                            ? 'pl-3 text-tertiary hover:text-primary'
                            : 'text-secondary hover:text-primary'
                        }
                      >
                        {h.text}
                      </a>
                    ))}
                </nav>
              </aside>
            ) : null}
          </main>
        </div>
      </div>

      {/* Anchor highlight pulse — keyframes inlined so we don't need a global stylesheet edit */}
      <style>{`
        @keyframes help-anchor-pulse-anim {
          0%   { background-color: rgba(255, 230, 100, 0.4); }
          100% { background-color: transparent; }
        }
        .help-anchor-pulse {
          animation: help-anchor-pulse-anim 1.8s ease-out;
          scroll-margin-top: 80px;
        }
        .help-html h1, .help-html h2, .help-html h3, .help-html h4 {
          scroll-margin-top: 80px;
        }
      `}</style>
    </>
  )
}


function pathFor(params: { section?: string; slug?: string; subSlug?: string }): string {
  const parts = ['/help', params.section, params.slug, params.subSlug].filter(Boolean)
  return parts.join('/')
}


function Breadcrumbs({ items }: { items: PageBreadcrumb[] }) {
  return (
    <nav className="mb-1 flex items-center gap-1 text-xs text-tertiary">
      {items.map((b, i) => (
        <span key={`${b.url}-${i}`} className="flex items-center gap-1">
          {i > 0 ? <ChevronRight size={10} /> : null}
          {i === items.length - 1 ? (
            <span className="text-secondary">{b.label}</span>
          ) : (
            <a href={b.url} className="text-link hover:underline">{b.label}</a>
          )}
        </span>
      ))}
    </nav>
  )
}
